<div class="page-head">
  <div>
    <h1>Config Check</h1>
    <div class="sub">Whether every API, service and app has the settings it needs — before you find out the hard way</div>
  </div>
  <div class="head-actions">
    <button type="button" class="act" (click)="load()" [disabled]="busy">
      {{ busy ? 'Checking…' : 'Re-check' }}
    </button>
  </div>
</div>

<div class="page-body">
  <div class="panel">
    @if (error) {
      <div class="note note-err">{{ error }}</div>
    }

    @if (busy && !loaded) {
      <div class="key-count">Asking every service…</div>
    }

    @if (loaded) {
      <div class="verdict" [class.verdict-ok]="status === 'Ok'"
           [class.verdict-warn]="status === 'Warning'"
           [class.verdict-err]="status === 'Error'">
        <span class="verdict-mark">{{ status === 'Ok' ? '✓' : status === 'Warning' ? '!' : '✕' }}</span>
        <span class="verdict-text">
          @if (status === 'Ok') {
            Every service reports its configuration complete.
          } @else {
            {{ problemCount }} thing{{ problemCount === 1 ? '' : 's' }} to look at across
            {{ services.length }} service{{ services.length === 1 ? '' : 's' }}.
          }
        </span>
        <span class="verdict-meta">{{ settingsChecked }} settings checked</span>
      </div>

      <div class="search-row">
        <input
          class="inp"
          type="text"
          [(ngModel)]="search"
          placeholder="Filter by key, service or description — e.g. InvalidationKey, Notification" />
        <label class="toggle">
          <input type="checkbox" [(ngModel)]="problemsOnly" />
          Problems only
        </label>
      </div>

      <!-- Cross-service findings first: a mismatched shared secret is invisible in any single
           service's report, because each side is individually correct. -->
      @if (findings.length > 0) {
        <div class="findings">
          <div class="panel-title">Across services</div>
          @for (finding of findings; track finding.title) {
            @if (!problemsOnly || finding.status !== 'Ok') {
              <div class="finding" [class]="statusClass(finding.status)">
                <div class="finding-head">
                  <span class="pill" [class]="statusClass(finding.status)">{{ finding.status }}</span>
                  <span class="finding-title">{{ finding.title }}</span>
                </div>
                <div class="finding-detail">{{ finding.detail }}</div>
              </div>
            }
          }
        </div>
      }

      @for (svc of services; track svc.service) {
        @if (!isHidden(svc)) {
          <div class="svc"
               [class.svc-down]="!svc.reachable"
               [class.svc-unconfigured]="svc.configured === false">
            <button type="button" class="svc-head" (click)="toggle(svc.service)">
              <span class="svc-caret">{{ isExpanded(svc.service) ? '▾' : '▸' }}</span>
              <span class="svc-name">{{ svc.service }}</span>

              @if (svc.environment) {
                <span class="svc-env">{{ svc.environment }}</span>
              }

              @if (svc.configured === false) {
                <span class="pill st-warn">not configured</span>
              } @else if (!svc.reachable) {
                <span class="pill st-err">unreachable</span>
              } @else if (problems(svc).length > 0) {
                <span class="pill" [class]="statusClass(svc.status)">
                  {{ problems(svc).length }} of {{ svc.settings.length }}
                </span>
              } @else {
                <span class="pill st-ok">{{ svc.settings.length }} ok</span>
              }
            </button>

            @if (svc.version) {
              <div class="svc-url">build {{ svc.version }}</div>
            }

            @if (svc.error) {
              <div class="note" [class.note-warn]="svc.configured === false || svc.reachable"
                   [class.note-err]="!svc.reachable && svc.configured !== false">{{ svc.error }}</div>
            }

            @if (isExpanded(svc.service) && svc.settings.length > 0) {
              @if (problems(svc).length > 0) {
                <div class="svc-actions">
                  <button type="button" class="act act-ghost" (click)="copyMissing(svc)">
                    Copy missing setting names
                  </button>
                </div>
              }

              <ul class="set-list">
                @for (setting of visibleSettings(svc); track setting.key) {
                  <li class="set-row" [class]="statusClass(setting.status)">
                    <div class="set-line">
                      <span class="pill" [class]="statusClass(setting.status)">{{ setting.status }}</span>
                      <code class="set-key">{{ setting.key }}</code>
                      @if (setting.severity !== 'Required') {
                        <span class="set-sev">{{ setting.severity }}</span>
                      }
                    </div>

                    <div class="set-desc">{{ setting.description }}</div>

                    @if (setting.detail) {
                      <div class="set-detail">{{ setting.detail }}</div>
                    }

                    <div class="set-meta">
                      @if (setting.secret) {
                        <span class="meta-val meta-secret">{{ secretSummary(setting) }}</span>
                      } @else if (setting.value) {
                        <span class="meta-val">{{ setting.value }}</span>
                      }
                      @if (setting.source) {
                        <span class="meta-src">from {{ setting.source }}</span>
                      }
                      @if (setting.status === 'Missing' || setting.status === 'Placeholder') {
                        <code class="meta-env">{{ appSettingName(setting.key) }}</code>
                      }
                    </div>
                  </li>
                }
              </ul>
            }
          </div>
        }
      }

      @if (hiddenCount > 0) {
        <div class="key-count">
          {{ hiddenCount }} service{{ hiddenCount === 1 ? '' : 's' }} hidden — nothing matched
          @if (problemsOnly) { <span>and nothing to report</span> }.
        </div>
      }
    }

    <div class="hint">
      Each service checks its <strong>own</strong> configuration and reports back: only the running
      process can see which provider a value actually came from, which is why
      <em>from Environment variable / App Service setting</em> versus <em>from appsettings.json</em>
      is worth reading — a committed file that outranks the setting you added in Azure looks
      identical to a setting that never took.
      <br /><br />
      Secret values are never sent. A secret comes back as its length and a short fingerprint, which
      is enough to prove two services hold the <strong>same</strong> secret — the failure a
      per-service check cannot see, because both sides are individually correct.
    </div>
  </div>
</div>
